import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
} from '@/shared/constants/pagination';
import {
  getMaxVisiblePages,
  getPageNumberButtonClass,
  getVisiblePages,
  paginationItemClassNameBySize,
  paginationItemSelectedClassName,
  type PaginationSize,
} from '@/shared/lib/paginationUtils';
import { cn } from '@/shared/lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import React from 'react';

interface SimplePaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: readonly number[];
  /**
   * default: 목록 화면용 48px RDS
   * sm: 모달 등 좁은 영역용 32px (줄바꿈 최소화)
   */
  size?: PaginationSize;
  className?: string;
}

/**
 * RDS Pagination.
 * 원형 아이템 + prev/next 화살표. `size="sm"`으로 모달용 소형 제공.
 * @see https://www.figma.com/design/wcgji99mZRPIytCZDjwqxe/RDS-v1.0?node-id=235-6281
 */
export const SimplePagination: React.FC<SimplePaginationProps> = ({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  size = 'default',
  className,
}) => {
  const isSm = size === 'sm';
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  const canChangePageSize = Boolean(onPageSizeChange);
  const visiblePages = getVisiblePages(
    page,
    totalPages,
    getMaxVisiblePages(size),
  );
  const itemClassName = paginationItemClassNameBySize[size];
  const chevronClassName = isSm ? 'size-4' : 'size-6';

  const pageButtons = (
    <div
      className={cn(
        'flex items-center justify-center',
        isSm ? 'shrink-0 gap-0.5' : 'flex-wrap gap-2',
      )}
    >
      <button
        type="button"
        className={itemClassName}
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
        aria-label="이전 페이지"
      >
        <ChevronLeft className={chevronClassName} strokeWidth={1.5} />
      </button>

      {visiblePages.map((pageNum) => {
        const isActive = pageNum === page;
        return (
          <button
            type="button"
            key={pageNum}
            className={cn(
              itemClassName,
              getPageNumberButtonClass(pageNum, size),
              isActive && paginationItemSelectedClassName,
            )}
            onClick={() => onPageChange(pageNum)}
            aria-label={`${pageNum}페이지`}
            aria-current={isActive ? 'page' : undefined}
          >
            {pageNum}
          </button>
        );
      })}

      <button
        type="button"
        className={itemClassName}
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
        aria-label="다음 페이지"
      >
        <ChevronRight className={chevronClassName} strokeWidth={1.5} />
      </button>
    </div>
  );

  if (isSm) {
    return (
      <div
        className={cn(
          'flex flex-nowrap items-center justify-between gap-2 border-t border-ruda-border-light bg-white px-3 py-2',
          className,
        )}
      >
        <p className="min-w-0 shrink truncate text-xs text-ruda-text-tertiary">
          <span className="font-medium text-ruda-text-primary">
            {start}-{end}
          </span>
          <span className="mx-0.5 text-ruda-text-disabled">/</span>
          총 {total.toLocaleString()}개
        </p>
        {pageButtons}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'border-t border-ruda-border-light bg-white px-4 py-4',
        className,
      )}
    >
      <div className="grid grid-cols-1 items-center gap-3 sm:grid-cols-[1fr_auto_1fr]">
        <p className="text-center text-sm text-ruda-text-tertiary sm:text-left">
          <span className="font-medium text-ruda-text-primary">
            {start}-{end}
          </span>
          <span className="mx-1 text-ruda-text-disabled">/</span>
          총 {total.toLocaleString()}개
        </p>

        {pageButtons}

        <div className="flex items-center justify-center gap-2 sm:justify-end">
          {canChangePageSize ? (
            <>
              <span className="text-sm text-ruda-text-tertiary">표시 개수</span>
              <Select
                value={String(pageSize)}
                onValueChange={(value) => onPageSizeChange?.(Number(value))}
              >
                {/* 숫자+개 — 좁은 고정폭에서 truncate(…)되지 않게 내용 너비로 맞춤 */}
                <SelectTrigger
                  size="md"
                  className="h-10 w-fit min-w-[4.5rem] shrink-0 py-2 [&>span]:flex-none [&>span]:overflow-visible [&>span]:whitespace-nowrap"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent align="end" className="min-w-[240px]">
                  {pageSizeOptions.map((option) => (
                    <SelectItem
                      key={option}
                      value={String(option)}
                      className="whitespace-nowrap"
                    >
                      {option}개
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          ) : (
            <span className="text-sm text-ruda-text-tertiary">
              {pageSize}개씩 표시
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS };
export type { PaginationSize };
